import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function pad(n) { return String(n).padStart(2, "0"); }

export default function MyShifts() {
  const [periods, setPeriods] = useState([]);
  const [periodId, setPeriodId] = useState("");
  const [shifts, setShifts] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/shift-periods").then((r) => {
        // Staff should only see periods that have been published to them.
        // "confirmed" is an internal manager state (still adjusting), so it
        // must not be visible until the manager publishes the whole period.
        const visible = r.data.filter(
          (p) => p.status === "published" || p.status === "closed"
        );
        setPeriods(visible);
        const published = visible.find((p) => p.status === "published");
        if (published) setPeriodId(published.id);
        else if (visible[0]) setPeriodId(visible[0].id);
      }),
      api.get("/departments").then((r) => setDepartments(r.data)),
    ]);
  }, []);

  useEffect(() => {
    if (!periodId) return;
    api.get(`/shifts?shift_period_id=${periodId}`).then((r) => setShifts(r.data));
  }, [periodId]);

  const period = periods.find((p) => p.id === periodId);
  const byDate = useMemo(() => {
    const m = {};
    for (const s of shifts) m[s.date] = s;
    return m;
  }, [shifts]);

  const days = period ? new Date(period.year, period.month, 0).getDate() : 0;
  const first = period ? new Date(period.year, period.month - 1, 1).getDay() : 0;
  const cells = [];
  for (let i = 0; i < first; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="p-4 space-y-4 max-w-md mx-auto" data-testid="my-shifts-page">
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">自分のシフト</h1>

      <Select value={periodId} onValueChange={setPeriodId}>
        <SelectTrigger data-testid="myshifts-period-select" className="bg-white"><SelectValue placeholder="対象月" /></SelectTrigger>
        <SelectContent>
          {periods.map((p) => (
            <SelectItem key={p.id} value={p.id}>{p.year}年{p.month}月分</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {period && (
        <Card className="p-3 bg-white">
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-500 mb-1">
            {["日", "月", "火", "水", "木", "金", "土"].map((w, i) => (
              <div key={i} className={i === 0 ? "text-red-500" : i === 6 ? "text-blue-500" : ""}>{w}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const dateStr = `${period.year}-${pad(period.month)}-${pad(d)}`;
              const s = byDate[dateStr];
              return (
                <div
                  key={i}
                  data-testid={`myshift-day-${d}`}
                  className={`aspect-square rounded-md text-[10px] flex flex-col items-center justify-center border ${
                    s ? "bg-green-50 border-green-300" : "bg-white border-slate-200"
                  }`}
                >
                  <span className="font-semibold text-slate-900 text-xs">{d}</span>
                  {s?.assignment && (
                    <span className="text-[9px] leading-tight font-bold text-emerald-800 bg-emerald-100 rounded px-1 mt-0.5 truncate max-w-full">
                      {s.assignment}
                    </span>
                  )}
                  {s && (
                    <span className="text-[8px] leading-tight text-green-700 mt-0.5 px-0.5">
                      {s.start_time}〜{s.end_time}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div className="space-y-2">
        <h2 className="text-base font-semibold text-slate-900">シフト一覧</h2>
        {shifts.length === 0 && (
          <p className="text-sm text-slate-500">確定シフトはまだありません</p>
        )}
        {shifts
          .sort((a, b) => a.date.localeCompare(b.date))
          .map((s) => {
            const dept = departments.find((d) => d.id === s.department_id);
            return (
              <div
                key={s.id}
                data-testid={`shift-row-${s.id}`}
                className={`bg-white border rounded-md p-3 ${
                  s.status === "changed" ? "border-yellow-300" : "border-slate-200"
                }`}
              >
                <div className="flex items-baseline justify-between">
                  <p className="font-semibold text-slate-900">{s.date}</p>
                  {s.assignment && (
                    <span className="text-[11px] font-bold text-emerald-800 bg-emerald-100 border border-emerald-200 rounded-full px-2 py-0.5">
                      {s.assignment}
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-600 mt-1">
                  {dept?.name || ""} {s.start_time}〜{s.end_time}
                </p>
                {s.status === "changed" && (
                  <span className="inline-block mt-1 text-[10px] text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-full px-2 py-0.5">
                    変更あり
                  </span>
                )}
              </div>
            );
          })}
      </div>

      {shifts.length > 0 && (
        <p className="text-xs text-slate-500 px-1">
          ※ 確定シフトの変更は担当者にメッセージでご相談ください。
        </p>
      )}
    </div>
  );
}
